import { Component, OnInit, Input, ViewChild } from '@angular/core';
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { UntypedFormGroup, Validators, UntypedFormControl } from "@angular/forms";
import { MatAutocompleteTrigger as MatAutocompleteTrigger, MatAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { OpenaiService, CompletionMessage } from "../openai.service";

interface Prompt {
  value: string | number;
  title: string;
  description: string;
  icon: string;
  content?: string;
}

export interface EmailCampaignAssistantData {
  id?: number;
  prompt_id: string | number;
  promptType: string;
  role: string;
  jd: string;
  tone: 'professional' | 'persuasive' | 'casual' | 'enthusiastic';
  /**
   * Response from the AI
   * NOTE: This is not a user input field
   */
  response?: Record<string, any>;
}

@Component({
    selector: 'app-email-campaign-assistant',
    templateUrl: './email-campaign-assistant.component.html',
    styleUrls: ['./email-campaign-assistant.component.scss'],
    standalone: false
})
export class EmailCampaignAssistantComponent implements OnInit {

  isGenerating: boolean = false;

  @Input('modal') isModal: boolean = false;

  @Input('data') data: EmailCampaignAssistantData = null;

  @Input('ladder') ladder: string[] = [];

  @ViewChild('promptDropDownTrigger', { read: MatAutocompleteTrigger }) promptDropDownTrigger: MatAutocompleteTrigger;

  currentPrompt: Prompt = null;

  prompts: Prompt[] = [
    { value: 'initial', title: 'Initial Email', description: 'Write an initial email to a potential candidate', icon: 'fa-mail-bulk' },
    { value: 'followup', title: 'Follow-up Email', description: 'Write a follow-up email to a potential candidate', icon: 'fa-mail-bulk' },
    { value: 'rejection', title: 'Rejection Email', description: 'Write a rejection email to a candidate', icon: 'fa-mail-bulk' },
    { value: 'interview', title: 'Interview Email', description: 'Write an email to a candidate to schedule an interview', icon: 'fa-mail-bulk' }
  ]

  promptJDMode: 'upload' | 'write' = 'write';

  private jdFile: File;

  get jdFileMeta() {
    if (this.jdFile) {
      return `${this.jdFile.name} (~${Math.round(this.jdFile.size / 1024)}kb)`;
    }
    return false;
  }

  aiPromptFormGroup = new UntypedFormGroup({
    id: new UntypedFormControl(null),
    prompt_id: new UntypedFormControl(null, [Validators.required]),
    promptType: new UntypedFormControl('JD', [Validators.required, (control) => {
      if (this.aiPromptFormGroup) {
        if (control.value === 'Role') {
          this.aiPromptFormGroup.get('role').setValidators([Validators.required]);
          this.aiPromptFormGroup.get('jd').setValidators(null)
        } else if (control.value === 'JD') {
          this.aiPromptFormGroup.get('role').setValidators(null);
          this.aiPromptFormGroup.get('jd').setValidators([Validators.required]);
        }
        this.aiPromptFormGroup.get('role').updateValueAndValidity();
        this.aiPromptFormGroup.get('jd').updateValueAndValidity();
      }
      return null;

    }]),
    role: new UntypedFormControl(''),
    jd: new UntypedFormControl(''),
    tone: new UntypedFormControl('professional', [Validators.required]),
  })

  constructor(private activeModalRef: NgbActiveModal, private readonly openaiService: OpenaiService) { }

  ngOnInit() {
    this.fetchPrompt();
  }

  fetchPrompt() {
    // Fetch prompt from server
    this.openaiService.getAssistant().subscribe((prompts) => {
      if (prompts) {
        this.prompts = prompts.map(p => ({ value: p.prompt_id, title: p.prompt_name, description: p.prompt_description, icon: p.icon || 'fa-mail-bulk' }));
      }
      if (this.data && Reflect.ownKeys(this.data).length) {
        this.aiPromptFormGroup.patchValue(this.data);
        if (this.data.prompt_id) {
          this.currentPrompt = this.prompts.find(p => p.value === this.data.prompt_id);
        }
      }
      this.currentPrompt = this.prompts[0]; // initial
      this.aiPromptFormGroup.get('prompt_id').patchValue(this.currentPrompt.value);
      this.populateContentOfCurrentPrompt();
    })
  }

  closeModal() {
    if (this.isModal) {
      this.activeModalRef.dismiss();
    }
  }

  submitModal(e: EmailCampaignAssistantData) {
    this.aiPromptFormGroup.disable();
    if (this.currentPrompt && this.currentPrompt.content) {
      this.isGenerating = true;
      let prompt = '';
      if (e.promptType === 'JD') {
        prompt = 'Job deacription:\n' + e.jd + '\n';
      }
      if (e.promptType === 'Role') {
        prompt = '\nRole: ' + e.role + '\n';
      }
      if (e.tone) {
        prompt += '\nTone: ' + e.tone + '\n';
      }
      prompt += "\nfollowing are the only valid placeholders: candidate.firstname,candidate.lastname,candidate.currentjobtitle,candidate.currentcompany,candidate.currentlocation,user.firstname,user.emailid,user.lastname\n and placeholders should be in {{}}. For example {{candidate.firstname}}.";
      this.openaiService.getCompletion([
        { content: this.currentPrompt.content, role: "system" },
        ...(this.ladder || []).map<CompletionMessage>((l) => ({ content: l, role: "user" })),
        { content: prompt, role: "user" },
      ]).subscribe((response) => {
        this.aiPromptFormGroup.enable();
        e.response = response;
        this.isGenerating = false;
        if (this.isModal) {
          this.activeModalRef.close(e);
        }
      });
    } else {
      if (this.isModal) {
        this.activeModalRef.close(e);
      }
    }
  }

  trySubmitModal() {
    this.aiPromptFormGroup.markAllAsTouched();
    this.aiPromptFormGroup.updateValueAndValidity();
    if (this.aiPromptFormGroup.valid) {
      this.submitModal(this.aiPromptFormGroup.value);
    }
  }

  toggleJDOption() {
    if (this.promptJDMode === 'upload') {
      this.aiPromptFormGroup.get('jd').setErrors({ required: false, invalidFileType: false });
      this.aiPromptFormGroup.get('jd').patchValue('', { emitEvent: false });
      this.jdFile = null;
    }
    this.promptJDMode = this.promptJDMode === 'upload' ? 'write' : 'upload';
  }

  jdUploadDragOver(e: DragEvent) {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }

  jdUploadDrop(e: DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    this.handleJDFile(file);
  }

  private handleJDFile(file: File) {
    if (file) {
      // check file type
      if (file.type !== 'application/pdf' && file.type !== 'text/plain' && file.type !== 'application/msword' && file.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        this.aiPromptFormGroup.get('jd').setErrors({ invalidFileType: true });
        return;
      }
      this.jdFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.aiPromptFormGroup.get('jd').patchValue(reader.result);
      }
      reader.readAsText(file);
    }
  }

  removeJDFile(e?: MouseEvent) {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    this.jdFile = null;
    this.aiPromptFormGroup.get('jd').patchValue('');
  }

  openUploadDialog() {
    if (this.jdFile) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.txt,.doc,.docx';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files[0];
      this.handleJDFile(file);
    }
    input.click();
  }

  togglePrompt(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    if (this.isGenerating) return;
    if (this.promptDropDownTrigger.panelOpen) {
      this.promptDropDownTrigger.closePanel();
    } else {
      this.promptDropDownTrigger.openPanel();
    }
  }

  promptSelected(e: MatAutocompleteSelectedEvent) {
    this.currentPrompt = this.prompts.find(p => p.value === e.option.value);
    this.populateContentOfCurrentPrompt();
  }

  populateContentOfCurrentPrompt() {
    if (this.currentPrompt && !this.currentPrompt.content) {
      this.openaiService.getAssistant(this.currentPrompt.value as number).subscribe((prompts) => {
        if (prompts && prompts.length) {
          const p = prompts[0];
          this.currentPrompt.content = p.prompt_text;
          this.prompts = this.prompts.map(pr => {
            if (pr.value === p.prompt_id) {
              pr.content = p.prompt_text;
            }
            return pr;
          });
        }
      });
    }
  }

}
